import React, { useState, useEffect } from 'react';
import * as S from './NewBarrierModal.style';
import ZaiaReaction from '../../../../../../../../zaiaComponents/zaiaReaction';
import { useTranslation } from 'react-i18next';
import Popup from 'reactjs-popup';
import FlorenzSelect from '../../../../../../../../florenzComponents/florenzSelect';
import { QUERY_GET_PROCESS_TYPES } from '../../../../../../../../../graphql/queries/Process';
import { useQuery, useMutation } from '@apollo/client';
import { QUERY_GET_USER_TO_PROCESS_BY_ISACEPTED } from '../../../../../../../../../graphql/queries/UserToProcess';
import { QUERY_GET_BARRIER_TYPES, QUERY_GET_BARRIER_SUBTYPES, MUTATION_INSERT_USER_TO_BARRIER } from '../../../../../../../../../graphql/mutations/Barriers';
import * as P from "../../../../../../../../../utils/pipes";
import { Actions } from "../../../../../../../../../utils/actionsIds";
import useRecordNavigation from '../../../../../../../../../hooks/useRecordNavigation';
import FlorenzSearchInput from '../../../../../../../../florenzComponents/florenceSearchInput';
import FlorenzButton from '../../../../../../../../florenzComponents/florenzButton';
import ZaiaModalResponseContent from '../../../../../../../../zaiaComponents/zaiaModalResponseContent';

// Assets
const logoZaiaLoading = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaReactions/singularity-health-loader.svg`;
const logoZaiaAlert = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaReactions/reaction-error-data.svg`;
const logoZaiaSuccess = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaReactions/reaction-succes-data.svg`;

const ENV = process.env.REACT_APP_ENV;

const NewBarrierModal = ({ onClose, patientId }) => {
  const { recordAction } = useRecordNavigation();
  const [step, setStep] = useState(patientId ? 2 : 1);
  const [selectedPatientId, setSelectedPatientId] = useState(patientId || null);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedSubtypeId, setSelectedSubtypeId] = useState("");
  
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState(3);
  const [filterProgram, setFilterProgram] = useState(null); 
  const [inputText, setInputText] = useState("");

  const [usersList, setUsersList] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const loadLimit = 20;
  
  const {t, i18n: { language }} = useTranslation("global"); 

  const { data: processTypes } = useQuery(QUERY_GET_PROCESS_TYPES);

  const [insertUserToBarrier, { loading: insertLoading }] = useMutation(MUTATION_INSERT_USER_TO_BARRIER);
  const [mutationSuccess, setMutationSuccess] = useState(false);
  const [mutationError, setMutationError] = useState(false);

  const handleCreateBarrier = async () => {
    try {
      setMutationError(false);
      setMutationSuccess(false);

      const selectedSubtype = barrierSubtypesData?.subTypeBarrier?.find(s => s.id === selectedSubtypeId);

      const objects = [{
        barrierStatus: "pending",
        description: description,
        processId: processId,
        severity: severity.toString(),
        subTypeBarrierId: selectedSubtypeId,
        userToProcessId: activePatient.id,
        userId: activePatient.userId || activePatient.user?.id,
        doctorId: activePatient.doctorId
      }];

      await insertUserToBarrier({
        variables: {
          objects
        },
        refetchQueries: ["QUERY_GET_USER_TO_BARRIERS_FILTERS"]
      });

      setMutationSuccess(true);
    } catch (e) {
      console.error(e);
      setMutationError(true);
    }
  };

  const where = 
    ENV === "production"
    ? {
      isAcepted: { _eq: true },
      stageId: { _nin: [
        "enroll", 
        "suspension_esquema_progresion",
        "completo_ciclos_tratamiento_programados", 
        "ReferredToAnotherInstitution", 
        "deceased", 
        "abandono_tratamiento", 
        "tres_contactos_telefonicos_fallidos", 
        "no_continua_seguimiento_decision_paciente",
        "suspension_tratamiento_cambio_esquema",
        "suspension_esquema_toxicidad",
        "Motivos_Otros"
      ] }
    }
    : {
      isAcepted: { _eq: true },
      stageId: { _nin: ["enroll"] }
    };

  if (filterProgram !== null && filterProgram !== undefined) {
    where.processId = { _eq: filterProgram };
  }

  if (inputText && inputText.trim() !== "") {
    const terms = inputText.trim().split(/\s+/);

    where._and = terms.map(term => ({
      _or: [
        { user: { name: { _ilike: `%${term}%` } } },
        { user: { lastname: { _ilike: `%${term}%` } } },
        { user: { email: { _ilike: `%${term}%` } } },
        { user: { documentNumber: { _ilike: `%${term}%` } } },
      ]
    }));
  }

  const { data: userToProcesses, loading: userToProcessesLoading, error: userToProcessesError, fetchMore } = useQuery(
    QUERY_GET_USER_TO_PROCESS_BY_ISACEPTED, 
    { 
      variables: { 
        where,
        limit: loadLimit, 
        offset: 0
      },
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      skip: step !== 1
    },
  );

  useEffect(() => {
    if (step !== 1) return;
    if (userToProcessesLoading) return;
    if (userToProcesses?.userToProcess) {
      setUsersList(userToProcesses.userToProcess);
      setOffset(userToProcesses.userToProcess.length);
      
      if (userToProcesses.userToProcess.length <= loadLimit) {
        setHasMore(userToProcesses.userToProcess.length === loadLimit);
      }
    }
  }, [userToProcessesLoading, userToProcesses, step]);

  const handleScroll = (e) => {
    if (step !== 1) return;
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    if (scrollHeight - scrollTop <= clientHeight + 100 && hasMore && !userToProcessesLoading && !isFetchingMore) {
      setIsFetchingMore(true);
      fetchMore({
        variables: {
          limit: loadLimit,
          offset: offset,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult || fetchMoreResult.userToProcess.length === 0) {
            return prev;
          }

          const existingIds = new Set(prev.userToProcess.map(p => p.id));
          const newItems = fetchMoreResult.userToProcess.filter(p => !existingIds.has(p.id));

          return {
            ...prev,
            userToProcess: [...prev.userToProcess, ...newItems]
          };
        }
      }).then(({ data }) => {
        if (!data || !data.userToProcess || data.userToProcess.length < loadLimit) {
          setHasMore(false);
        }
        setIsFetchingMore(false);
      }).catch(() => {
        setIsFetchingMore(false);
      });
    }
  };

  const selectedPatient = usersList.find(p => p.id === selectedPatientId);

  const { data: patientData } = useQuery(QUERY_GET_USER_TO_PROCESS_BY_ISACEPTED, {
    variables: {
      where: { id: { _eq: patientId } },
      limit: 1,
      offset: 0
    },
    skip: !patientId || !!selectedPatient
  });

  const activePatient = selectedPatient || patientData?.userToProcess?.[0];
  const processId = activePatient?.process?.id || activePatient?.processId;

  const { data: barrierTypesData, loading: barrierTypesLoading } = useQuery(QUERY_GET_BARRIER_TYPES, {
    variables: {
      processId: processId
    },
    skip: step < 2 || !processId
  });

  const { data: barrierSubtypesData, loading: barrierSubtypesLoading } = useQuery(QUERY_GET_BARRIER_SUBTYPES, {
    variables: {
      typeBarrierId: selectedCategoryId
    },
    skip: step < 3 || !selectedCategoryId
  });

  const renderHeader = () => (
    <S.Header>
      <button className="icon-btn close" onClick={onClose}>
        <i className="icon zaia-icono-cerrar"></i>
      </button>
      <div class="title">Nueva barrera</div >
      <button className="icon-btn disable">
        <div className="icon zaia-icono-bloqueado">⊘</div>
      </button>
    </S.Header>
  );

  console.log("akjsdfkjasd", P.dataTranslation(barrierTypesData?.typeBarrier?.find(c => c.id === selectedCategoryId)?.name, language));
  

  const renderSummary = () => (
    <S.SelectedPatientSummary>
      {
        selectedPatientId ?
        <div className="patient-info">
          <img src={activePatient?.user?.picture || "https://i.pravatar.cc/150"} alt="avatar" />
          <p>Barrera para paciente <strong>{activePatient ? `${activePatient?.user?.name} ${activePatient?.user?.lastname}` : "Seleccionado"}</strong>{step !== 4 && ", fase 4"}</p>
        </div>
        : <div className="patient-info">
          <i className="patient-info__icon icon zaia-i-patient"></i>
          <div className="patient-info__text">
            Seleccione paciente
          </div>
        </div>    
      }
      
      {step === 2 && (
        <div className="subtitle-step2">Seleccione barrera <span className="error">*</span></div>
      )}
      {step === 3 && (
        <div className="subtitle-step3">
          Barrera {barrierTypesData?.typeBarrier?.find(c => c.id === selectedCategoryId)?.name ? P.dataTranslation(barrierTypesData.typeBarrier.find(c => c.id === selectedCategoryId).name, language) : ''}, <strong>Seleccione subtipo</strong> <span className="error">*</span>
        </div>
      )}
      {step === 4 && (
        <div className="subtitle-step4">
          Barrera {barrierTypesData?.typeBarrier?.find(c => c.id === selectedCategoryId)?.name ? P.dataTranslation(barrierTypesData.typeBarrier.find(c => c.id === selectedCategoryId).name, language) : ''}, <strong>
            {
               barrierSubtypesData?.subTypeBarrier?.find(s => s.id === selectedSubtypeId)?.name ? P.dataTranslation(barrierSubtypesData.subTypeBarrier.find(s => s.id === selectedSubtypeId).name, language) : ''
            }
          </strong> <i className="icon zaia-i-check-1"></i>
        </div>
      )}
    </S.SelectedPatientSummary>
  );

  const renderSearchSection = () => (
    <S.SearchSection>
      <FlorenzSearchInput
        placeholder={t("globally.search")}
        forWhiteBackgroud={false}
        getText={(text) => setInputText(text)}
        borderanimation={false}
      />
      <div className="program-select">
        <Popup
          trigger={
            <div>
              <FlorenzSelect
                placeholder={t("globally.allPrograms")}
                label={t("globally.program")}
                xsSize={true}
                forWhiteBackgroud={false}
                items={
                  processTypes?.process?.map((item) => ({
                    value: item.id,
                    text: P.dataTranslation(item.processName, language),
                    img: item.img
                  })) || []
                }
                value={filterProgram}
                noDropdown={true}
              />                        
            </div>
          }
          closeOnDocumentClick
          keepTooltipInside
          position={["bottom center"]}
          nested
        >
          {
            close => (
              <S.ProgramSelector>
                <div className="header">
                  <div className="header__label">{t("globally.program")}</div>
                  {/* <div className="header__label">{t("globally.patients")}</div> */}
                </div>
                <div className="list-programs">
                  <div className={`program-item ${filterProgram === null && "program-item--actived"}`}
                    onClick={() => {
                      setFilterProgram(null);
                      recordAction(Actions.AINAVIGATOR.FILTER_BARRIERS_PROGRAM);
                      close();
                    }}
                  >
                    <div className="program-item__name">{t("globally.all")}</div>
                    {/* <div className="program-item__quantity">
                      <i className="program-item__icon icon zaia-i-patient-mangement"></i>
                      <div className="program-item__number">{userToProcessesCount?.userToProcess_aggregate.aggregate.count || 0}</div>
                    </div> */}
                  </div>
                  {
                    processTypes?.process.map((item) => {
                      if(item.userToProcesses?.length > 0) {
                        return (                                 
                          <div className={`program-item ${filterProgram === item.id && "program-item--actived"}`}
                            key={item.id}
                            onClick={() => {
                              setFilterProgram(item.id);
                              recordAction(Actions.AINAVIGATOR.FILTER_BARRIERS_PROGRAM, null, item.id);
                              close();
                            }}
                          >
                            <div className="program-item__name">{P.dataTranslation(item.processName, language)}</div>
                            {/* <div className="program-item__quantity">
                              <i className="program-item__icon icon zaia-i-patient-mangement"></i>
                              <div className="program-item__number">{item.userToProcesses?.length}</div>
                            </div> */}
                          </div>
                        )
                      }
                    })
                  }
                </div>
              </S.ProgramSelector>
            )
          }
        </Popup> 
      </div>
    </S.SearchSection>
  );

  const renderStep1 = () => (
    <S.PatientList onScroll={handleScroll}>
      {usersList.map((patient) => (
        <S.PatientCard 
          key={patient.id} 
          selected={selectedPatientId === patient.id}
          onClick={() => setSelectedPatientId(patient.id)}
        >
          <div className="radio-circle">
            {selectedPatientId === patient.id && <div className="inner-circle" />}
          </div>
          <img src={patient.user?.picture || "https://i.pravatar.cc/150"} alt={`${patient.user?.name}`} />
          <div className="info">
            <span className="name">{`${patient.user?.name} ${patient.user?.lastname}`}</span>
            <span className="email">{patient.user?.email}</span>
            <span className="email">{P.dataTranslation(patient.process?.processName, language)}</span>
          </div>
          <div className="id">{patient.user?.documentNumber}</div>
        </S.PatientCard>
      ))}
      {isFetchingMore && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
          <ZaiaReaction widthimg="40px" zaiaImg={logoZaiaLoading}/>
        </div>
      )}
      {usersList.length === 0 && !userToProcessesLoading && (
        <div style={{ textAlign: 'center', padding: '20px', color: '#707582' }}>
          No se encontraron pacientes
        </div>
      )}
      {userToProcessesLoading && usersList.length === 0 && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
          <ZaiaReaction widthimg="60px" zaiaImg={logoZaiaLoading}/>
        </div>
      )}
    </S.PatientList>
  );

  const renderStep2 = () => {
    if (barrierTypesLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
          <ZaiaReaction widthimg="60px" zaiaImg={logoZaiaLoading}/>
        </div>
      );
    }

    const barrierTypes = barrierTypesData?.typeBarrier || [];

    return (
      <S.BarrierCategoryList>
        {barrierTypes.map(cat => (
          <S.CategoryCard 
            key={cat.id}
            selected={selectedCategoryId === cat.id}
            onClick={() => {
              setSelectedCategoryId(cat.id);
              setSelectedSubtypeId("");
            }}
          >
             <h4>{P.dataTranslation(cat.name, language)}</h4>
             <p>{P.dataTranslation(cat.description, language)}</p>
          </S.CategoryCard>
        ))}
        {barrierTypes.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#707582' }}>
            No hay tipos de barreras configurados
          </div>
        )}
      </S.BarrierCategoryList>
    );
  };

  const renderStep3 = () => {
    if (barrierSubtypesLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
          <ZaiaReaction widthimg="60px" zaiaImg={logoZaiaLoading}/>
        </div>
      );
    }

    const barrierSubtypes = barrierSubtypesData?.subTypeBarrier || [];

    return (
      <S.BarrierCategoryList>
        {barrierSubtypes.map(sub => (
          <S.CategoryCard 
            key={sub.id}
            selected={selectedSubtypeId === sub.id}
            onClick={() => setSelectedSubtypeId(sub.id)}
          >
             <h4>{P.dataTranslation(sub.name, language)}</h4>
             <p>{P.dataTranslation(sub.description, language)}</p>
          </S.CategoryCard>
        ))}
        {barrierSubtypes.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#707582' }}>
            No hay subtipos de barreras configurados para este tipo
          </div>
        )}
      </S.BarrierCategoryList>
    );
  };

  const renderStep4 = () => (
    <S.FormSection>
      <div className="textarea-container">
        <textarea 
          placeholder="Describa la barrera.."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      
      <div className="severity-container">
        <label>Nivel de severidad de barrera</label>
        <div className={`severity-selector level-${severity}`}>
          <div 
            className={`severity-option ${severity === 1 ? 'active' : ''}`}
            onClick={() => setSeverity(1)}
          >1</div>
          <div 
            className={`severity-option ${severity === 2 ? 'active' : ''}`}
            onClick={() => setSeverity(2)}
          >2</div>
          <div 
            className={`severity-option ${severity === 3 ? 'active' : ''}`}
            onClick={() => setSeverity(3)}
          >3</div>
        </div>
      </div>
    </S.FormSection>
  );

  const renderFooter = () => (
    <S.Footer>
      {step === 1 ? (
        //  <button className="btn-back" disabled>
        //     Volver <i className="icon zaia-icono-candado">🔒</i>
        //  </button>
         <FlorenzButton
            size="s"
            typeStyle={"primary"}
            text={"Volver"}
            disabled={true}
            iconSize={"14"}
            icon={"zaia-i-lock-chat"}
            onClick={() => {
              
            }}
          />
      ) : (
        //  <button className="btn-back active" onClick={() => {
        //     if (step === 4) setStep(3);
        //     else if (step === 3) setStep(2);
        //     else {
        //       if (patientId) onClose();
        //       else setStep(1);
        //     }
        //  }}>
        //     Volver
        //  </button>
         <FlorenzButton
            size="s"
            typeStyle={"secondaryWhite"}
            text={"Volver"}
            onClick={() => {
              if (step === 4) setStep(3);
              else if (step === 3) setStep(2);
              else {
                if (patientId) onClose();
                else setStep(1);
              }
            }}
          />
      )}

      {step === 1 ? (
         <FlorenzButton
            size="s"
            typeStyle={"primary"}
            text={"Siguiente"}
            disabled={!selectedPatientId}
            iconSize={"14"}
            icon={!selectedPatientId && "zaia-i-lock-chat"}
            onClick={() => setStep(2)}
          />
      ) : step === 2 ? (
         <FlorenzButton
            size="s"
            typeStyle={"primary"}
            text={"Siguiente"}
            disabled={!selectedCategoryId}
            iconSize={"14"}
            icon={!selectedCategoryId && "zaia-i-lock-chat"}
            onClick={() => setStep(3)}
          />
      ) : step === 3 ? (
         <FlorenzButton
            size="s"
            typeStyle={"primary"}
            text={"Siguiente"}
            disabled={!selectedSubtypeId}
            iconSize={"14"}
            icon={!selectedSubtypeId && "zaia-i-lock-chat"}
            onClick={() => setStep(4)}
          />
      ) : (
         <FlorenzButton
            size="s"
            typeStyle={"primary"}
            text={"Crear barrera"}
            disabled={!description}
            iconSize={"14"}
            icon={!description && "zaia-i-lock-chat"}
            onClick={handleCreateBarrier}
          />
      )}
    </S.Footer>
  );

  if (insertLoading) {
    return (
      <S.NewBarrierModal>
        <div className="modal-background" onClick={() => {}} />
        <div className="modal-container">
          <div className="modal-container__reaction" style={{ display: 'flex', justifyContent: 'center' }}>
            <ZaiaReaction widthimg="150px" zaiaImg={logoZaiaLoading} /> 
          </div>
        </div>
      </S.NewBarrierModal>
    );
  }

  if (mutationSuccess) {
    return (
      <S.NewBarrierModal>
        <div className="modal-background" onClick={() => onClose()} />
        <div className="modal-container">
          <ZaiaModalResponseContent
            formSuccess={true}
            title={"Barrera creada exitosamente"}
            button1Text={t("globally.accept")}
            button1={() => onClose()}
          />
        </div>
      </S.NewBarrierModal>
    );
  }

  if (mutationError) {
    return (
      <S.NewBarrierModal>
        <div className="modal-background" onClick={() => onClose()} />
        <div className="modal-container">
          <ZaiaModalResponseContent
            formSuccess={false}
            title={"Ocurrió un error al crear la barrera"}
            button1Text={"Volver"}
            button1={() => setMutationError(false)}
          />
        </div>
      </S.NewBarrierModal>
    );
  }

  return (
    <S.NewBarrierModal>
      <div className="modal-background" onClick={() => onClose()}>
      </div>
      <div className="modal-container">
        {renderHeader()}
        {renderSummary()}
        
        {step !== 4 && renderSearchSection()}

        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
        
        {renderFooter()}
      </div>
    </S.NewBarrierModal>
  );
};

export default NewBarrierModal;
